import { useMemo, useSyncExternalStore } from 'react'
import {
  useMarketplaceOrderSnapshot,
  useOrderMessageSnapshot,
  useOrderThreadSnapshot,
} from '../../generated/prototypeRuntime'
import type { ChowRole } from './ChooseChowContext'
import { isUnpaidMealPlanHold } from './orderDisplay'
import { formatRFC3339UTC, isAfterTimestamp } from '@ovasabi/frontend-kit'

/**
 * "Has anyone written to me?" — the signal that was missing entirely.
 *
 * Order chat had no unread state at all, on either side: a customer could write
 * about their order and the only way the chef would ever find out was to open
 * Profile → Inbox and go looking. From the customer's side that is
 * indistinguishable from the message never arriving.
 *
 * There is no server-side read receipt, so "read" is a per-device mark: the
 * timestamp of the newest message in a thread at the moment that thread was
 * last open. A thread counts as unread when someone else's newest message is
 * newer than that mark. Marks live in localStorage, so this is deliberately
 * per-device and never travels — it is a nudge, not a delivery receipt.
 */
const STORAGE_KEY = 'chow_thread_read'

type ReadMarks = Readonly<Record<string, string>>

const listeners = new Set<() => void>()
let marks: ReadMarks | null = null

/** Cached so `getSnapshot` is referentially stable between real changes. */
const getMarks = (): ReadMarks => {
  if (marks) return marks
  try {
    marks = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') as ReadMarks
  } catch {
    marks = {}
  }
  return marks
}

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * Mark a thread read up to `lastCreatedAt` (the newest message the reader has
 * actually been shown). A no-op when it would move the mark backwards, so an
 * out-of-order render cannot resurrect messages the reader has seen.
 */
export const markThreadRead = (threadId: string, lastCreatedAt: string): void => {
  if (!threadId || !lastCreatedAt) return
  const current = getMarks()
  if (current[threadId] && !isAfterTimestamp(lastCreatedAt, current[threadId])) return
  marks = { ...current, [threadId]: lastCreatedAt }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(marks))
  } catch {
    /* private mode / storage full — the badge just stops persisting */
  }
  listeners.forEach((listener) => listener())
}

const useReadMarks = (): ReadMarks => useSyncExternalStore(subscribe, getMarks, getMarks)

/**
 * How many of MY order conversations have something new in them from someone
 * else.
 *
 * Scoped to this actor's own orders on purpose: the projection is scoped to the
 * organization, not to one person, so counting every thread in the store would
 * count strangers' conversations.
 */
export const useUnreadThreadCount = (profileId: string, role: ChowRole): number => {
  const orders = useMarketplaceOrderSnapshot() as { records: readonly Record<string, unknown>[] }
  const threads = useOrderThreadSnapshot() as { records: readonly Record<string, unknown>[] }
  const messages = useOrderMessageSnapshot() as { records: readonly Record<string, unknown>[] }
  const readMarks = useReadMarks()

  return useMemo(() => {
    if (!profileId) return 0
    const mine = role === 'chef' ? 'merchant_profile_id' : 'customer_profile_id'
    const myOrderIds = new Set(
      orders.records
        .filter((order) => !isUnpaidMealPlanHold(order) && String(order[mine] ?? '') === profileId)
        .map((order) => String(order.id ?? '')),
    )
    const myThreadIds = new Set(
      threads.records
        .filter((thread) => myOrderIds.has(String(thread.order_id ?? '')))
        .map((thread) => String(thread.id ?? '')),
    )
    if (myThreadIds.size === 0) return 0

    // Newest message per thread that this actor did not write.
    const newestFromOthers = new Map<string, string>()
    for (const message of messages.records) {
      const threadId = String(message.thread_id ?? '')
      if (!myThreadIds.has(threadId)) continue
      if (String(message.sender_profile_id ?? '') === profileId) continue
      const at = String(message.created_at ?? '')
      const prev = newestFromOthers.get(threadId)
      if (!prev || isAfterTimestamp(at, prev)) newestFromOthers.set(threadId, at)
    }

    let unread = 0
    for (const [threadId, at] of newestFromOthers) {
      const mark = readMarks[threadId]
      if (!mark || isAfterTimestamp(at, mark)) unread += 1
    }
    return unread
  }, [orders.records, threads.records, messages.records, readMarks, profileId, role])
}

export interface UnreadOrderInfo {
  unreadCount: number
  lastMessage?: string
  lastSenderClass?: string
  lastCreatedAt?: string
}

/**
 * Returns a dictionary mapping each order ID to its unread message count
 * and last message metadata for the current user.
 */
export const useUnreadOrdersMap = (
  profileId: string,
  role: ChowRole,
): Record<string, UnreadOrderInfo> => {
  const orders = useMarketplaceOrderSnapshot() as { records: readonly Record<string, unknown>[] }
  const threads = useOrderThreadSnapshot() as { records: readonly Record<string, unknown>[] }
  const messages = useOrderMessageSnapshot() as { records: readonly Record<string, unknown>[] }
  const readMarks = useReadMarks()

  return useMemo(() => {
    if (!profileId) return {}
    const mine = role === 'chef' ? 'merchant_profile_id' : 'customer_profile_id'
    const myOrderIds = new Set(
      orders.records
        .filter((order) => !isUnpaidMealPlanHold(order) && String(order[mine] ?? '') === profileId)
        .map((order) => String(order.id ?? '')),
    )

    // Map thread_id -> order_id
    const threadToOrder = new Map<string, string>()
    for (const thread of threads.records) {
      const orderId = String(thread.order_id ?? '')
      if (myOrderIds.has(orderId)) {
        threadToOrder.set(String(thread.id ?? ''), orderId)
      }
    }

    const map: Record<string, UnreadOrderInfo> = {}

    for (const message of messages.records) {
      const threadId = String(message.thread_id ?? '')
      const orderId = threadToOrder.get(threadId) || String(message.order_id ?? '')
      if (!orderId || !myOrderIds.has(orderId)) continue

      const isFromOther = String(message.sender_profile_id ?? '') !== profileId
      const createdAt = String(message.created_at ?? '')
      const mark = readMarks[threadId]
      const isUnread = isFromOther && (!mark || isAfterTimestamp(createdAt, mark))

      if (!map[orderId]) {
        map[orderId] = { unreadCount: 0 }
      }

      if (isUnread) {
        map[orderId].unreadCount += 1
      }

      const prev = map[orderId].lastCreatedAt
      if (!prev || isAfterTimestamp(createdAt, prev)) {
        map[orderId].lastMessage = String(message.body ?? '')
        map[orderId].lastSenderClass = String(message.sender_class ?? '')
        map[orderId].lastCreatedAt = formatRFC3339UTC(message.created_at) || createdAt
      }
    }

    return map
  }, [orders.records, threads.records, messages.records, readMarks, profileId, role])
}
